import { collection, query, getDocs, addDoc, serverTimestamp, doc, setDoc, getDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { ApiClient } from './api-client.service';
import { GmailService, RawEmailData, RawEmailAttachment } from './gmail.service';
import { GeminiBookingParser, ParsedBookingData } from './gemini-booking-parser.service';
import { TripService } from './trip.service';
import { AccommodationService } from './accommodation.service';
import { FlightService } from './flight.service';
import { CarRentalService } from './car-rental.service';
import { ActivityService } from './activity.service';
import { BookingType, ApprovalStatus, Trip, AccommodationType, FlightStatus, Accommodation, ActivityType, EmailAttachmentInfo } from '../models/index';
import { COUNTRIES, getCurrencyForCountryCode } from '../data/countries';
import EntityEnrichmentService from './entity-enrichment.service';
import { NotificationService, NotificationType } from './notification.service';
import { parseWallClockIso, normalizeWallClockIso } from '../utils/wallClockTime';

// ──────────────────────────────────────────────────────────────
// Email Processing Pipeline — orchestrates the full flow:
//   1. Fetch emails from Gmail (FROM the logged-in user)
//   2. Filter booking-related emails
//   3. Deduplicate against already-processed emails
//   4. Parse with Gemini AI
//   5. Save booking records + auto-match to trips
//   6. Create trip entities when auto-matched
// ──────────────────────────────────────────────────────────────

export interface ProcessingResult {
    totalEmails: number;
    bookingRelated: number;
    duplicatesSkipped: number;
    parsed: number;
    failed: number;
    matched: number;
    newTripsCreated: number;
    errors: string[];
}

/** Live progress update emitted by processEmails during a scan. */
export interface EmailScanProgress {
    /** Current pipeline phase */
    phase: 'fetching' | 'filtering' | 'processing' | 'done';
    /** Human-readable status message */
    message: string;
    /** Emails handled so far (0 when not yet known) */
    current: number;
    /** Total emails to process (0 when not yet known) */
    total: number;
}

export type EmailScanProgressCallback = (progress: EmailScanProgress) => void;

/** Options for a one-off scan. */
export interface EmailScanOptions {
    /**
     * Ignore the `lastEmailCheckTime` cursor and re-fetch the whole lookback
     * window instead. Every scan — including one that found nothing — pushes
     * that cursor to "now", so a mail is only ever offered once: if its import
     * went wrong (wrong type, deleted booking), an ordinary scan can never see
     * it again. Emails whose booking still exists are skipped by the
     * rawEmailId / confirmation-number dedupe, so a deep scan only re-imports
     * what is genuinely missing.
     */
    deep?: boolean;
}

/** How far back a deep scan looks. Gmail resolves this server-side. */
const DEEP_SCAN_LOOKBACK_DAYS = 30;

export class EmailProcessingService {
    private static parser = new GeminiBookingParser();

    /** Returns the mobile-compatible collection path: users/{userId}/email_bookings */
    private static getCollectionPath(userId: string): string {
        return `users/${userId}/email_bookings`;
    }

    /**
     * Guards against two scans running at once. The scheduler (app startup) and
     * the Booking Inbox page can both trigger a scan nearly simultaneously; if
     * they overlap, both read "0 already processed" before either writes and end
     * up importing the same email — and creating the same trip — twice.
     */
    private static _inFlight: Promise<ProcessingResult> | null = null;

    /** Mode of the in-flight run — a deep scan must not be served a normal one. */
    private static _inFlightDeep = false;

    /**
     * Main entry point — scan Gmail and process all booking emails.
     * Concurrent callers share a single in-flight run to prevent double imports.
     * @param onProgress Optional callback called at each meaningful step so the
     *   UI can display live progress (phase, message, current / total counts).
     */
    static async processEmails(
        userId: string,
        userEmail: string,
        onProgress?: EmailScanProgressCallback,
        options?: EmailScanOptions,
    ): Promise<ProcessingResult> {
        const deep = options?.deep === true;
        // Only share the in-flight run when it covers the same window — a deep
        // scan asked for a wider one and must not be handed a cursor scan.
        if (this._inFlight && (!deep || this._inFlightDeep)) {
            console.log('📧 processEmails already running — reusing the in-flight scan');
            return this._inFlight;
        }
        if (this._inFlight) await this._inFlight.catch(() => undefined);
        this._inFlightDeep = deep;
        this._inFlight = this._processEmailsInternal(userId, userEmail, onProgress, deep)
            .finally(() => { this._inFlight = null; this._inFlightDeep = false; });
        return this._inFlight;
    }

    private static async _processEmailsInternal(
        userId: string,
        userEmail: string,
        onProgress?: EmailScanProgressCallback,
        deep = false,
    ): Promise<ProcessingResult> {
        const result: ProcessingResult = {
            totalEmails: 0,
            bookingRelated: 0,
            duplicatesSkipped: 0,
            parsed: 0,
            failed: 0,
            matched: 0,
            newTripsCreated: 0,
            errors: [],
        };

        try {
            // 1. Get the last check time for this user to only fetch new emails
            const lastCheckTime = deep ? null : await this.getLastEmailCheckTime(userId);
            if (deep) {
                console.log(`📧 Deep scan — ignoring the cursor, looking back ${DEEP_SCAN_LOOKBACK_DAYS} days`);
            } else if (lastCheckTime) {
                console.log(`📧 Last email check: ${lastCheckTime.toISOString()}`);
            } else {
                console.log(`📧 First email scan for user ${userId}`);
            }

            // 2. Fetch emails from Gmail (only after lastCheckTime)
            console.log(`📧 Fetching emails from ${userEmail}...`);
            onProgress?.({ phase: 'fetching', message: 'Fetching emails from Gmail…', current: 0, total: 0 });
            const emails = await GmailService.fetchEmailsForUser(
                userEmail,
                lastCheckTime || undefined,
                deep ? { lookbackDays: DEEP_SCAN_LOOKBACK_DAYS } : undefined,
            );
            result.totalEmails = emails.length;
            console.log(`📧 Found ${emails.length} emails`);

            if (emails.length === 0) {
                // Even with no results, update the check time so we don't re-scan the same window
                await this.setLastEmailCheckTime(userId);
                onProgress?.({ phase: 'done', message: 'No new emails found', current: 0, total: 0 });
                return result;
            }

            // 3. Process ALL fetched (user-forwarded) emails — we no longer drop
            // non-keyword mail. Unclear ones are still parsed and surfaced as
            // unrecognized (not added to a trip) so no forwarded mail is missed.
            // isBookingRelated is kept only for the reported count.
            onProgress?.({ phase: 'filtering', message: `Checking ${emails.length} email${emails.length !== 1 ? 's' : ''} for booking content…`, current: 0, total: emails.length });
            const bookingEmails = emails;
            result.bookingRelated = emails.filter(email =>
                GmailService.isBookingRelated(email.subject, email.body)
            ).length;
            console.log(`📧 ${result.bookingRelated}/${emails.length} are keyword booking-related; parsing all`);

            if (bookingEmails.length === 0) {
                await this.setLastEmailCheckTime(userId);
                onProgress?.({ phase: 'done', message: 'No booking emails found', current: 0, total: 0 });
                return result;
            }

            // 4. Get existing email IDs and confirmation numbers to check for duplicates
            const existing = await this.getExistingEmailIds(userId);
            console.log(`📧 ${existing.emailIds.size} emails already processed, ${existing.confirmationNumbers.size} confirmation numbers known`);

            // 4. Fetch user's trips for auto-matching
            const trips = await TripService.getUserTrips(userId);
            console.log(`📧 User has ${trips.length} trips for matching`);

            // 5. Process each booking email
            const totalBooking = bookingEmails.length;
            let processedCount = 0;
            for (const email of bookingEmails) {
                processedCount++;
                onProgress?.({
                    phase: 'processing',
                    message: `Parsing email: "${email.subject || email.id}"`,
                    current: processedCount,
                    total: totalBooking,
                });
                try {
                    // Check for duplicates — only skip if already in NEW collection
                    if (existing.emailIds.has(email.id)) {
                        result.duplicatesSkipped++;
                        continue;
                    }

                    // Parse with Gemini. Prefer whichever body is richer: many
                    // itineraries ship a sparse text/plain stub ("view in
                    // browser") with the real fare/price table only in the HTML,
                    // so a plain-text-only body silently loses totalPrice.
                    const htmlText = email.htmlBody ? GmailService.stripHtml(email.htmlBody) : '';
                    const bodyForParsing =
                        htmlText.length > (email.body?.length || 0) ? htmlText : email.body;

                    // Forwarded e-tickets often carry the booking details (price,
                    // times, PNR) only inside an attached PDF/image, with little
                    // or nothing in the body. Send the best attachment inline so
                    // the model can read those fields — mirrors the mobile app.
                    const aiAttachment = await this.buildAttachmentPayloadForAi(
                        email.id,
                        email.attachments
                    );
                    const parsed = await this.parser.parseBookingEmail(
                        email.subject,
                        bodyForParsing,
                        email.sender,
                        aiAttachment ?? undefined
                    );

                    if (!parsed) {
                        result.failed++;
                        continue;
                    }

                    // Unclear type: still bring the mail in (surfaced for review)
                    // but never auto-add it to a trip.
                    const isUnrecognized =
                        parsed.bookingType === BookingType.UNKNOWN ||
                        parsed.bookingType === BookingType.UNRECOGNIZED;

                    // Also check for duplicates by confirmation number (only in NEW collection)
                    if (parsed.confirmationNumber && existing.confirmationNumbers.has(parsed.confirmationNumber)) {
                        console.log(`📧 Skipping duplicate by confirmation number: ${parsed.confirmationNumber}`);
                        result.duplicatesSkipped++;
                        existing.emailIds.add(email.id);
                        continue;
                    }

                    result.parsed++;
                    // Track this email + confirmation so subsequent emails in same batch don't duplicate
                    existing.emailIds.add(email.id);
                    if (parsed.confirmationNumber) existing.confirmationNumbers.add(parsed.confirmationNumber);

                    // Download + store any file attachments (e.g. the e-ticket PDF)
                    // and link the source email so created entities (flights) can
                    // open the original mail/attachments later.
                    const attachmentInfos = await this.downloadAndStoreAttachments(
                        userId,
                        email.id,
                        email.attachments
                    );
                    parsed.sourceEmailId = email.id;
                    if (attachmentInfos.length > 0) parsed.attachmentInfos = attachmentInfos;

                    // Find matching trip (never for unrecognized — it stays unattached)
                    const matchedTrip = isUnrecognized ? null : this.findMatchingTrip(parsed, trips, userId);

                    // Save booking to Firestore (new collection)
                    const bookingId = await this.saveBooking(
                        userId,
                        email,
                        parsed,
                        matchedTrip?.id,
                        attachmentInfos
                    );

                    // Unrecognized bookings are surfaced but intentionally not
                    // added to any trip (and no trip is auto-created for them).
                    if (isUnrecognized) {
                        continue;
                    }

                    if (matchedTrip && bookingId) {
                        try {
                            await this.attachBookingToTrip(userId, bookingId, matchedTrip, parsed);
                            result.matched++;
                        } catch (entityError) {
                            console.warn(`⚠️ Matched trip but failed to create entity:`, entityError);
                            result.errors.push(`Entity creation failed for email ${email.id}: ${entityError}`);
                        }
                    } else if (bookingId) {
                        // No date-overlap match. Before auto-creating, check whether a
                        // trip already covers the exact same dates (e.g. created by an
                        // earlier booking) and attach to it instead of duplicating.
                        try {
                            const { startDate, endDate } = this.deriveTripDates(parsed);
                            const sameDatesTrip = this.findTripWithSameDates(startDate, endDate, trips, userId);
                            if (sameDatesTrip) {
                                await this.attachBookingToTrip(userId, bookingId, sameDatesTrip, parsed);
                                result.matched++;
                            } else if (parsed.bookingType === BookingType.ACTIVITY) {
                                // Activities never spawn a new trip — an attraction/
                                // ticket alone isn't enough to define one. Leave it
                                // pending for the user to assign manually.
                                console.log(`📧 Activity booking ${bookingId} has no matching trip - left pending`);
                            } else if (!this.bookingHasTripDetails(parsed)) {
                                // Not enough detail (missing dates or destination) to
                                // build a meaningful trip. Leave the booking pending for
                                // manual review instead of spawning a junk "New Trip".
                                // Mirrors mobile's _bookingHasTripDetails guard.
                                console.log(`📧 Booking ${bookingId} lacks dates/destination - skipping auto trip creation, left pending`);
                            } else {
                                // Auto-create a new trip (mirrors the mobile flow).
                                const newTripId = await this.createTripFromBooking(userId, parsed, bookingId);
                                result.newTripsCreated++;
                                // Add the new trip to the in-memory list so later emails in
                                // this same batch attach to it instead of creating duplicates.
                                trips.push({
                                    id: newTripId,
                                    name: this.generateTripName(parsed),
                                    startDate,
                                    endDate,
                                    countries: [],
                                    regions: [],
                                } as unknown as Trip);
                            }
                        } catch (createError) {
                            console.warn(`⚠️ Failed to auto-create trip from booking:`, createError);
                            result.errors.push(`Trip creation failed for email ${email.id}: ${createError}`);
                        }
                    }

                    // Small delay between API calls
                    await new Promise(resolve => setTimeout(resolve, 500));
                } catch (emailError: any) {
                    console.error(`❌ Failed to process email ${email.id}:`, emailError);
                    result.failed++;
                    result.errors.push(`Email ${email.id}: ${emailError.message}`);
                }
            }

            // Update last processed timestamp (per-user)
            await this.setLastEmailCheckTime(userId);
            await this.updateLastProcessedTime(userId);

            onProgress?.({ phase: 'done', message: 'Scan complete', current: totalBooking, total: totalBooking });
            console.log(`✅ Processing complete:`, result);
            return result;
        } catch (error: any) {
            const msg = String(error?.message || error);
            if (msg.includes('not configured') || msg.includes('GmailNotConfigured')) {
                console.info('ℹ️ Gmail not configured — email scan skipped');
            } else {
                console.error('❌ Email processing pipeline error:', error);
            }
            result.errors.push(error.message);
            return result;
        }
    }

    // ─── Duplicate detection ─────────────────────────────────────

    /**
     * Collect existing email IDs AND confirmation numbers from the
     * mobile-compatible subcollection (`users/{userId}/email_bookings`).
     */
    private static async getExistingEmailIds(userId: string): Promise<{
        emailIds: Set<string>;
        confirmationNumbers: Set<string>;
    }> {
        const emailIds = new Set<string>();
        const confirmationNumbers = new Set<string>();

        try {
            const mobileQ = query(
                collection(db, this.getCollectionPath(userId))
            );
            const mobileSnap = await getDocs(mobileQ);
            mobileSnap.forEach(doc => {
                const data = doc.data();
                if (data.rawEmailId) emailIds.add(data.rawEmailId);
                if (data.emailId) emailIds.add(data.emailId);
                if (data.externalBookingId) confirmationNumbers.add(data.externalBookingId);
                if (data.confirmationNumber) confirmationNumbers.add(data.confirmationNumber);
            });
        } catch (error) {
            console.warn('⚠️ Could not fetch existing email IDs:', error);
        }

        return { emailIds, confirmationNumbers };
    }

    // ─── Trip matching (date overlap scoring) ────────────────────

    static findMatchingTrip(parsed: ParsedBookingData, trips: Trip[], userId?: string): Trip | null {
        if (!parsed.checkInDate && !parsed.departureTime && !parsed.pickupDate) {
            return null;
        }

        // Only consider trips the user can actually write to. getUserTrips also
        // returns trips shared with the user as a read-only viewer; auto-attaching
        // a booking to one of those would pass matching but then fail silently on
        // the entity write (Firestore rules only allow owner/participant writes),
        // leaving the booking marked "added" with nothing in the trip.
        // (Mirrors mobile EmailBookingService._userCanWriteTrip.)
        const candidateTrips = userId
            ? trips.filter(t => this.userCanWriteTrip(t, userId))
            : trips;

        // Determine the booking date range
        let bookingStart: Date | null = null;
        let bookingEnd: Date | null = null;

        if (parsed.checkInDate) {
            bookingStart = new Date(parsed.checkInDate);
        }
        if (parsed.checkOutDate) {
            bookingEnd = new Date(parsed.checkOutDate);
        }
        if (parsed.departureTime && !bookingStart) {
            bookingStart = new Date(parsed.departureTime);
        }
        if (parsed.arrivalTime && !bookingEnd) {
            bookingEnd = new Date(parsed.arrivalTime);
        }
        if (parsed.pickupDate && !bookingStart) {
            bookingStart = new Date(parsed.pickupDate);
        }
        if (parsed.returnDate && !bookingEnd) {
            bookingEnd = new Date(parsed.returnDate);
        }

        if (!bookingStart) return null;
        // If no end date, use start date + 1 day
        if (!bookingEnd) {
            bookingEnd = new Date(bookingStart);
            bookingEnd.setDate(bookingEnd.getDate() + 1);
        }

        // Normalize to whole calendar days (local midnight). Without this a
        // point-in-time booking such as a same-day flight — departure and
        // arrival on the same date, with arrival often parsed as date-only
        // midnight — yields zero or negative sub-day overlap and is wrongly
        // rejected, spawning a duplicate trip even though the flight falls
        // squarely inside an existing trip's date range.
        const dayMs = 1000 * 60 * 60 * 24;
        const startOfDay = (d: Date) => {
            const x = new Date(d);
            x.setHours(0, 0, 0, 0);
            return x.getTime();
        };
        const bStart = startOfDay(bookingStart);
        const bEnd = startOfDay(bookingEnd!);

        let bestTrip: Trip | null = null;
        let bestScore = 0;

        for (const trip of candidateTrips) {
            const tStart = startOfDay(new Date(trip.startDate));
            const tEnd = startOfDay(new Date(trip.endDate));

            // Check for day-level date overlap (inclusive of both endpoints).
            if (bStart > tEnd || bEnd < tStart) continue;

            // Inclusive day-count overlap — always ≥ 1 for any overlapping
            // range, so a booking contained within a trip still scores > 0.
            const overlapDays =
                (Math.min(bEnd, tEnd) - Math.max(bStart, tStart)) / dayMs + 1;

            // Bonus points for destination match
            let locationBonus = 0;
            if (parsed.city || parsed.country) {
                const tripLocations = trip.countries
                    ?.map(c => `${c.name || ''} ${c.code || ''}`.toLowerCase())
                    .join(' ') || '';
                const tripRegions = trip.regions
                    ?.map(r => r.name?.toLowerCase() || '')
                    .join(' ') || '';
                const fullTripText = `${trip.name} ${tripLocations} ${tripRegions}`.toLowerCase();

                if (parsed.city && fullTripText.includes(parsed.city.toLowerCase())) {
                    locationBonus += 5;
                }
                if (parsed.country && fullTripText.includes(parsed.country.toLowerCase())) {
                    locationBonus += 3;
                }
            }

            const score = overlapDays + locationBonus;
            if (score > bestScore) {
                bestScore = score;
                bestTrip = trip;
            }
        }

        // Only return if we have a reasonable match (at least partial day overlap)
        return bestScore > 0 ? bestTrip : null;
    }

    /**
     * Find a trip whose start and end fall on the same calendar days as the
     * given range. Used to avoid creating a duplicate trip when one already
     * exists for the same dates.
     */
    private static findTripWithSameDates(startDate: Date, endDate: Date, trips: Trip[], userId?: string): Trip | null {
        const sameDay = (a: Date, b: Date) =>
            a.getFullYear() === b.getFullYear() &&
            a.getMonth() === b.getMonth() &&
            a.getDate() === b.getDate();
        // Same write-permission guard as findMatchingTrip: never attach to a
        // trip the user can only view.
        const candidateTrips = userId
            ? trips.filter(t => this.userCanWriteTrip(t, userId))
            : trips;
        for (const trip of candidateTrips) {
            const ts = new Date(trip.startDate);
            const te = new Date(trip.endDate);
            if (sameDay(ts, startDate) && sameDay(te, endDate)) return trip;
        }
        return null;
    }

    /**
     * Whether the user may write trip content (accommodations, flights, car
     * rentals, activities) to this trip — i.e. they are the owner or a
     * participant. Trips only *shared* with the user (sharedToUserIds) are
     * read-only and must be excluded from auto-attach matching to avoid silent
     * write failures. Mirrors mobile EmailBookingService._userCanWriteTrip.
     */
    private static userCanWriteTrip(trip: Trip, userId: string): boolean {
        const ownerId = (trip as any).ownerId ?? (trip as any).owner;
        const participantIds: string[] = (trip as any).participantIds ?? [];
        return ownerId === userId || participantIds.includes(userId);
    }

    /**
     * Whether a parsed booking carries enough detail (a real start date AND
     * some destination hint) to build a meaningful trip. Without these,
     * createTripFromBooking would fall back to today's date / "New Trip" and
     * spawn a junk trip, so such bookings are left pending for manual review.
     * Mirrors mobile EmailBookingService._bookingHasTripDetails.
     */
    private static bookingHasTripDetails(parsed: ParsedBookingData): boolean {
        const hasStart = !!(parsed.checkInDate || parsed.departureTime || parsed.pickupDate);
        if (!hasStart) return false;
        const hasDestination = !!(
            (parsed.country && parsed.country.trim()) ||
            (parsed.city && parsed.city.trim()) ||
            (parsed.propertyAddress && parsed.propertyAddress.trim()) ||
            (parsed.fromLocation && parsed.fromLocation.trim()) ||
            (parsed.toLocation && parsed.toLocation.trim()) ||
            (parsed.pickupLocation && parsed.pickupLocation.trim())
        );
        return hasDestination;
    }

    /**
     * Attach an already-saved booking to an existing trip: create the trip
     * entity, mark the booking approved/attached, and notify the user.
     */
    private static async attachBookingToTrip(
        userId: string,
        bookingId: string,
        trip: Trip,
        parsed: ParsedBookingData,
    ): Promise<void> {
        // Dedup logic inside each creator prevents duplicate entities.
        await this.createTripEntity(trip.id, userId, parsed);
        const bookingRef = doc(db, this.getCollectionPath(userId), bookingId);
        await setDoc(bookingRef, {
            approvalStatus: ApprovalStatus.APPROVED,
            matchedTripId: trip.id,
            status: 'attached',
            processedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        }, { merge: true });
        await EmailProcessingService._sendBookingNotification(
            userId, parsed, trip.id, trip.name, true
        );
    }

    // ─── Save booking to Firestore (mobile-compatible format) ────

    private static async saveBooking(
        userId: string,
        email: RawEmailData,
        parsed: ParsedBookingData,
        tripId?: string,
        attachmentInfos: EmailAttachmentInfo[] = []
    ): Promise<string> {
        // Generate a unique ID for the booking
        const bookingId = doc(collection(db, '_')).id;

        // Build mobile-compatible booking data
        // Mobile stores dates as ISO 8601 strings, uses enum .name values
        const bookingData: Record<string, any> = {
            id: bookingId,
            userId,
            bookingType: parsed.bookingType, // 'accommodation', 'flight', 'carRental'
            approvalStatus: tripId ? ApprovalStatus.APPROVED : ApprovalStatus.PENDING,
            vendor: parsed.vendor || '',
            externalBookingId: parsed.confirmationNumber || null,
            propertyName: parsed.propertyName || null,
            propertyAddress: parsed.propertyAddress || null,
            city: parsed.city || null,
            country: parsed.country || null,
            checkInDate: parsed.checkInDate ? new Date(parsed.checkInDate).toISOString() : null,
            checkOutDate: parsed.checkOutDate ? new Date(parsed.checkOutDate).toISOString() : null,
            cancelUntil: null,
            totalPrice: parsed.totalPrice || null,
            currency: parsed.currency || 'USD',
            guestName: parsed.guestName || null,
            guestEmail: null,
            rawEmailId: email.id,
            attachmentUrls: attachmentInfos.map((a) => a.url),
            attachmentInfos: attachmentInfos.map((a) => ({ ...a })),
            parsingConfidence: parsed.confidence || 0,
            createdAt: new Date().toISOString(),
            processedAt: tripId ? new Date().toISOString() : null,
            status: tripId ? 'attached' : 'unmatched',
            matchedTripId: tripId || null,
            location: null,
            notes: (parsed.bookingType === BookingType.UNRECOGNIZED ||
                parsed.bookingType === BookingType.UNKNOWN)
                ? 'Booking type could not be determined automatically, so this was not added to a trip. Review the details and add it manually if needed.'
                : null,
            aiReasoning: null,
            // Additional fields for web-compatibility
            rawEmailSubject: email.subject,
            rawEmailSender: email.sender,
            rawEmailSnippet: email.body ? email.body.substring(0, 500) : null,
            // Store HTML body for rich email display (truncate to 100KB to avoid Firestore limits)
            rawEmailHtml: email.htmlBody ? email.htmlBody.substring(0, 100000) : null,
            confirmationNumber: parsed.confirmationNumber || null,
            airline: parsed.airline || null,
        };

        // Flight details — stored as nested object matching mobile format
        if (parsed.bookingType === BookingType.FLIGHT) {
            const segments = (parsed.segments || []).map((s: any) => ({
                airline: s.airline || parsed.airline || null,
                flightNumber: s.flightNumber || parsed.flightNumber || null,
                departure: s.fromLocation || parsed.fromLocation || '',
                arrival: s.toLocation || parsed.toLocation || '',
                // Wall-clock, not toISOString(): a flight time is local to its
                // airport, and a UTC-stamped value shifts when the mobile app
                // (or another timezone) reads the same booking.
                departureTime: normalizeWallClockIso(s.departureTime),
                arrivalTime: normalizeWallClockIso(s.arrivalTime),
                gate: s.gate || null,
                terminal: s.terminal || null,
                seat: s.seat || null,
            }));

            // If no segments from parser, build one from the top-level fields
            if (segments.length === 0) {
                segments.push({
                    airline: parsed.airline || null,
                    flightNumber: parsed.flightNumber || null,
                    departure: parsed.fromLocation || '',
                    arrival: parsed.toLocation || '',
                    departureTime: normalizeWallClockIso(parsed.departureTime),
                    arrivalTime: normalizeWallClockIso(parsed.arrivalTime),
                    gate: null,
                    terminal: null,
                    seat: null,
                });
            }

            bookingData.flightDetails = {
                confirmationNumber: parsed.confirmationNumber || null,
                passengerName: parsed.guestName || '',
                segments,
            };
        }

        // Car rental details — stored as nested object matching mobile format
        if (parsed.bookingType === BookingType.CAR_RENTAL) {
            bookingData.carRentalDetails = {
                company: parsed.company || parsed.vendor || '',
                vehicleType: parsed.vehicleType || null,
                pickupLocation: parsed.pickupLocation || '',
                returnLocation: parsed.returnLocation || parsed.pickupLocation || '',
                pickupDate: parsed.pickupDate ? new Date(parsed.pickupDate).toISOString() : null,
                returnDate: parsed.returnDate ? new Date(parsed.returnDate).toISOString() : null,
                confirmationNumber: parsed.confirmationNumber || null,
                driverName: parsed.driverName || parsed.guestName || '',
            };
        }

        // Strip undefined values (Firestore rejects them)
        const cleanData = Object.fromEntries(
            Object.entries(bookingData).filter(([, v]) => v !== undefined)
        );

        // Save to mobile-compatible path: users/{userId}/email_bookings/{bookingId}
        const docRef = doc(db, this.getCollectionPath(userId), bookingId);
        await setDoc(docRef, cleanData);

        // Also save the raw email to the shared raw_emails collection
        // so mobile can display the original email content
        await this.saveRawEmail(userId, email, attachmentInfos);

        console.log(`✅ Booking saved: ${bookingId} (type: ${parsed.bookingType})`);
        return bookingId;
    }

    // ─── Create trip entities (accommodation/flight/car-rental) ──

    static async createTripEntity(
        tripId: string,
        userId: string,
        parsed: ParsedBookingData
    ): Promise<string | null> {
        switch (parsed.bookingType) {
            case BookingType.ACCOMMODATION:
                return this.createAccommodationEntity(tripId, parsed);
            case BookingType.FLIGHT:
                return this.createFlightEntity(tripId, userId, parsed);
            case BookingType.CAR_RENTAL:
                return this.createCarRentalEntity(tripId, userId, parsed);
            case BookingType.ACTIVITY:
                return this.createActivityEntity(tripId, parsed);
            default:
                return null;
        }
    }

    // Add an activity/attraction booking as a structured Activity on the trip.
    private static async createActivityEntity(
        tripId: string,
        parsed: ParsedBookingData
    ): Promise<string> {
        const title = (parsed.propertyName && parsed.propertyName.trim())
            || (parsed.vendor && parsed.vendor.trim())
            || 'Activity';

        const addressText = (parsed.propertyAddress && parsed.propertyAddress.trim())
            || [parsed.city, parsed.country].filter(v => v && v.trim()).join(', ');

        const activityData = {
            title,
            description: '',
            date: parsed.checkInDate ? new Date(parsed.checkInDate) : undefined,
            endDate: parsed.checkOutDate ? new Date(parsed.checkOutDate) : undefined,
            type: ActivityType.ATTRACTION,
            highlightImages: [] as string[],
            regions: [],
            location: addressText
                ? { latitude: 0, longitude: 0, address: addressText }
                : undefined,
            price: parsed.totalPrice ?? undefined,
            currency: parsed.currency ?? undefined,
            isBooked: true,
            isSelected: false,
            isPaid: false,
            tags: [] as string[],
            // Link back to the source booking email + its stored attachments
            // (e.g. the ticket PDF/image) so the "Original Email" section renders
            // on the activity, mirroring flights/accommodations/car-rentals.
            sourceEmailId: parsed.sourceEmailId || null,
            attachmentInfos: parsed.attachmentInfos && parsed.attachmentInfos.length > 0
                ? parsed.attachmentInfos.map((a) => ({ ...a }))
                : [],
        };

        const id = await ActivityService.createActivity(tripId, activityData as any);

        // Enrich with real coordinates + cover image (fire-and-forget).
        EntityEnrichmentService
            .enrichActivity(tripId, id, title, parsed.propertyAddress)
            .catch((e: unknown) => console.warn('Activity enrichment failed:', e));

        return id;
    }

    private static async createAccommodationEntity(tripId: string, parsed: ParsedBookingData): Promise<string> {
        // Dedup: match an accommodation for the same booking (by confirmation
        // number, else by name + check-in date). When found, merge in any new
        // details — crucially a real, non-zero price from a later payment/
        // receipt email — instead of creating a second entry or leaving the
        // original stuck at price 0.
        try {
            const existing = await AccommodationService.getAccommodations(tripId);
            const accName = (parsed.propertyName || '').toLowerCase().trim();
            const confirmation = (parsed.confirmationNumber || '').trim();
            const duplicate = existing.find(a => {
                const existingConfirmation = (a.originalId || '').toString().trim();
                if (confirmation && existingConfirmation && confirmation === existingConfirmation) {
                    return true;
                }
                const nameMatch = accName && a.name?.toLowerCase().trim() === accName;
                const dateMatch = parsed.checkInDate && a.checkInDate &&
                    new Date(parsed.checkInDate).toDateString() === new Date(a.checkInDate).toDateString();
                return nameMatch && dateMatch;
            });
            if (duplicate && duplicate.id) {
                const updates: Partial<Accommodation> = {};
                // Prefer a real price if the existing one is still zero.
                if ((!duplicate.price || duplicate.price <= 0) && (parsed.totalPrice || 0) > 0) {
                    updates.price = parsed.totalPrice as number;
                    if (parsed.currency) updates.currency = parsed.currency;
                }
                // Fill in details the existing entry is missing.
                if (!duplicate.checkInDate && parsed.checkInDate) updates.checkInDate = new Date(parsed.checkInDate);
                if (!duplicate.checkOutDate && parsed.checkOutDate) updates.checkOutDate = new Date(parsed.checkOutDate);
                if (!duplicate.originalId && confirmation) updates.originalId = confirmation;
                // Backfill the source-email link/attachments if the existing entry
                // predates them (e.g. created before this feature or from a body-only email).
                if (!duplicate.sourceEmailId && parsed.sourceEmailId) updates.sourceEmailId = parsed.sourceEmailId;
                if ((!duplicate.attachmentInfos || duplicate.attachmentInfos.length === 0) &&
                    parsed.attachmentInfos && parsed.attachmentInfos.length > 0) {
                    updates.attachmentInfos = parsed.attachmentInfos.map((a) => ({ ...a }));
                }

                if (Object.keys(updates).length > 0) {
                    await AccommodationService.updateAccommodation(tripId, duplicate.id, updates);
                    console.log(`🔄 Merged booking into existing accommodation "${duplicate.name}" (${duplicate.id}):`, Object.keys(updates));
                } else {
                    console.log(`⚠️ Accommodation already exists in trip: "${duplicate.name}" — skipping entity creation`);
                }
                return duplicate.id;
            }
        } catch (e) {
            console.warn('Could not check for duplicate accommodations:', e);
        }

        // Build data matching mobile Accommodation.toMap() format
        const accId = doc(collection(db, '_')).id;
        const data: any = {
            id: accId,
            name: parsed.propertyName || 'Accommodation',
            type: AccommodationType.HOTEL,
            website: '',
            isIncludeBreakfast: false,
            price: parsed.totalPrice || 0,
            currency: parsed.currency || 'USD',
            isBooked: true,
            isPaid: false,
            // Confirmation number — dedup key so a later payment/receipt email
            // for the same booking merges instead of creating a duplicate.
            originalId: parsed.confirmationNumber || null,
            // Link back to the source booking email + its stored attachments.
            sourceEmailId: parsed.sourceEmailId || null,
            attachmentInfos: parsed.attachmentInfos && parsed.attachmentInfos.length > 0
                ? parsed.attachmentInfos.map((a) => ({ ...a }))
                : [],
            notes: parsed.vendor ? `Booked via ${parsed.vendor}` : null,
            images: null,
            cancelUntil: parsed.cancellationDeadline ? new Date(parsed.cancellationDeadline).toISOString() : null,
            freeCancellation: parsed.freeCancellation ?? (!!parsed.cancellationDeadline),
            cancellationDeadline: parsed.cancellationDeadline ? new Date(parsed.cancellationDeadline).toISOString() : null,
            region: null,
            // location is REQUIRED by mobile Accommodation.fromMap()
            location: {
                id: accId,
                name: parsed.propertyName || 'Accommodation',
                lat: 0.0,
                lng: 0.0,
                address: parsed.propertyAddress || '',
                isActivity: false,
                isSelected: false,
                isBooked: true,
                isCarRental: false,
                isDropOff: false,
            },
        };

        // Store dates as ISO strings to match mobile format
        if (parsed.checkInDate) data.checkInDate = new Date(parsed.checkInDate).toISOString();
        if (parsed.checkOutDate) data.checkOutDate = new Date(parsed.checkOutDate).toISOString();

        // 🔍 Log the full data structure being written to Firestore
        console.log('[createAccommodationEntity] Writing to Firestore:', {
            name: data.name,
            freeCancellation: data.freeCancellation,
            cancelUntil: data.cancelUntil,
            cancellationDeadline: data.cancellationDeadline,
            parsedFreeCancellation: parsed.freeCancellation,
            parsedCancellationDeadline: parsed.cancellationDeadline,
        });

        const id = await AccommodationService.createAccommodation(tripId, data);
        console.log(`✅ Accommodation entity created: ${id} → now starting enrichment`);

        // Enrich with real geocoordinates and a cover photo (fire-and-forget)
        EntityEnrichmentService.enrichAccommodation(
            tripId, id,
            parsed.propertyName || '',
            parsed.propertyAddress
        ).catch(e => console.warn('[EmailProcessing] Enrichment skipped:', e));

        return id;
    }

    private static async createFlightEntity(
        tripId: string,
        userId: string,
        parsed: ParsedBookingData
    ): Promise<string> {
        // Dedup: check if flights with same route/number already exist
        let existingFlights: any[] = [];
        try {
            existingFlights = await FlightService.getFlights(tripId);
        } catch (e) {
            console.warn('Could not check for duplicate flights:', e);
        }

        const isDuplicateFlight = (from: string, to: string, flightNum?: string) => {
            return existingFlights.some(f => {
                if (flightNum && f.flightNumber === flightNum) return true;
                const fromMatch = from && f.fromLocation?.toLowerCase().trim() === from.toLowerCase().trim();
                const toMatch = to && f.toLocation?.toLowerCase().trim() === to.toLowerCase().trim();
                return fromMatch && toMatch;
            });
        };

        // If we have segments, create a flight for each segment
        if (parsed.segments && parsed.segments.length > 0) {
            let firstId = '';
            let priceAssigned = false; // put the booking's total on the first segment only
            for (const segment of parsed.segments) {
                const segFrom = segment.fromLocation || parsed.fromLocation || '';
                const segTo = segment.toLocation || parsed.toLocation || '';
                const segFlight = segment.flightNumber || parsed.flightNumber;
                if (isDuplicateFlight(segFrom, segTo, segFlight)) {
                    console.log(`⚠️ Flight segment ${segFrom} → ${segTo} already exists — skipping`);
                    continue;
                }
                const data: any = {
                    title: `${segFrom || '?'} → ${segTo || '?'}`,
                    fromLocation: segFrom,
                    toLocation: segTo,
                    firstName: parsed.guestName?.split(' ')[0] || '',
                    lastName: parsed.guestName?.split(' ').slice(1).join(' ') || '',
                    flightNumber: segment.flightNumber || parsed.flightNumber || undefined,
                    airline: segment.airline || parsed.airline || undefined,
                    reservationNumber: parsed.confirmationNumber || undefined,
                    gate: segment.gate || undefined,
                    terminal: segment.terminal || undefined,
                    seat: segment.seat || undefined,
                    sourceEmailId: parsed.sourceEmailId || undefined,
                    attachmentInfos: parsed.attachmentInfos && parsed.attachmentInfos.length > 0
                        ? parsed.attachmentInfos.map((a) => ({ ...a }))
                        : undefined,
                    status: FlightStatus.SCHEDULED,
                    // Booking total belongs to the whole reservation; assign it to the
                    // first segment so it isn't double-counted across legs.
                    price: !priceAssigned ? (parsed.totalPrice || 0) : 0,
                    currency: parsed.currency || 'USD',
                    isPaid: false,
                    uploadedBy: userId,
                };
                priceAssigned = true;

                // Wall-clock parse: keep the airport-local time the ticket
                // states, whatever offset the parser may have attached.
                if (segment.departureTime) data.departureTime = parseWallClockIso(segment.departureTime);
                else if (parsed.departureTime) data.departureTime = parseWallClockIso(parsed.departureTime);

                if (segment.arrivalTime) data.arrivalTime = parseWallClockIso(segment.arrivalTime);
                else if (parsed.arrivalTime) data.arrivalTime = parseWallClockIso(parsed.arrivalTime);

                const id = await FlightService.addFlight(tripId, data);
                if (!firstId) firstId = id;
            }
            console.log(`✅ ${parsed.segments.length} flight segment(s) created`);
            return firstId;
        }

        // Single flight
        const singleFrom = parsed.fromLocation || '';
        const singleTo = parsed.toLocation || '';
        if (isDuplicateFlight(singleFrom, singleTo, parsed.flightNumber)) {
            console.log(`⚠️ Flight ${singleFrom} → ${singleTo} already exists — skipping`);
            return '';
        }
        const data: any = {
            title: `${singleFrom || '?'} → ${singleTo || '?'}`,
            fromLocation: singleFrom,
            toLocation: singleTo,
            firstName: parsed.guestName?.split(' ')[0] || '',
            lastName: parsed.guestName?.split(' ').slice(1).join(' ') || '',
            flightNumber: parsed.flightNumber || undefined,
            airline: parsed.airline || undefined,
            reservationNumber: parsed.confirmationNumber || undefined,
            sourceEmailId: parsed.sourceEmailId || undefined,
            attachmentInfos: parsed.attachmentInfos && parsed.attachmentInfos.length > 0
                ? parsed.attachmentInfos.map((a) => ({ ...a }))
                : undefined,
            status: FlightStatus.SCHEDULED,
            price: parsed.totalPrice || 0,
            currency: parsed.currency || 'USD',
            isPaid: false,
            uploadedBy: userId,
        };

        if (parsed.departureTime) data.departureTime = parseWallClockIso(parsed.departureTime);
        if (parsed.arrivalTime) data.arrivalTime = parseWallClockIso(parsed.arrivalTime);

        const id = await FlightService.addFlight(tripId, data);
        console.log(`✅ Flight entity created: ${id}`);
        return id;
    }

    private static async createCarRentalEntity(
        tripId: string,
        userId: string,
        parsed: ParsedBookingData
    ): Promise<string> {
        // Dedup: check if car rental with same confirmation or company+dates already exists
        try {
            const existing = await CarRentalService.getCarRentals(tripId);
            const duplicate = existing.find(c => {
                if (parsed.confirmationNumber && c.confirmationNumber === parsed.confirmationNumber) return true;
                const companyMatch = (parsed.company || parsed.vendor || '').toLowerCase() ===
                    (c.company || '').toLowerCase();
                const dateMatch = parsed.pickupDate && c.pickupDate &&
                    new Date(parsed.pickupDate).toDateString() === new Date(c.pickupDate).toDateString();
                return companyMatch && dateMatch;
            });
            if (duplicate) {
                console.log(`⚠️ Car rental already exists in trip: "${duplicate.company}" — skipping entity creation`);
                return duplicate.id || '';
            }
        } catch (e) {
            console.warn('Could not check for duplicate car rentals:', e);
        }

        const data: any = {
            userId,
            company: parsed.company || parsed.vendor || 'Unknown',
            vehicleType: parsed.vehicleType || '',
            pickupLocation: parsed.pickupLocation || '',
            returnLocation: parsed.returnLocation || parsed.pickupLocation || '',
            pickupDate: parsed.pickupDate ? new Date(parsed.pickupDate) : new Date(),
            returnDate: parsed.returnDate ? new Date(parsed.returnDate) : new Date(),
            confirmationNumber: parsed.confirmationNumber || '',
            driverName: parsed.driverName || parsed.guestName || '',
            price: parsed.totalPrice || 0,
            currency: parsed.currency || 'USD',
            isPaid: false,
            // Link back to the source booking email + its stored attachments.
            sourceEmailId: parsed.sourceEmailId || null,
            attachmentInfos: parsed.attachmentInfos && parsed.attachmentInfos.length > 0
                ? parsed.attachmentInfos.map((a) => ({ ...a }))
                : [],
        };

        const id = await CarRentalService.addCarRental(tripId, data);
        console.log(`✅ Car rental entity created: ${id}`);
        return id;
    }

    // ─── Create a new trip from a booking when no match is found ──

    static async createTripFromBooking(
        userId: string,
        parsed: ParsedBookingData,
        bookingId: string
    ): Promise<string> {
        const { startDate, endDate } = this.deriveTripDates(parsed);

        // Resolve the destination country so the trip isn't created blank.
        // Prefer the parser's own country; if it's missing (common for
        // round-trip flights), geocode the destination as a deterministic fallback.
        let country = this.resolveCountry(parsed);
        if (!country) {
            const hint = this.pickDestinationHint(parsed);
            if (hint) {
                country = await this.resolveCountryFromDestination(hint);
            }
        }
        const countries = country ? [country] : [];

        // Name the trip after the resolved country (falls back to city) so
        // e.g. a Bratislava flight becomes "Slovakia - Aug 2026", not "Bratislava…".
        const tripName = this.generateTripName(parsed, country);
        // Prefer the booking's own currency; otherwise derive it from the
        // destination country (falls back to USD inside the helper).
        const defaultCurrency =
            parsed.currency || (country ? getCurrencyForCountryCode(country.code) : 'USD');

        const tripData: any = {
            name: tripName,
            ownerId: userId,
            startDate,
            endDate,
            participantIds: [],
            sharedToUserIds: [],
            countries,
            ...(country ? { mainCountry: country } : {}),
            regions: [],
            highlightImages: [],
            mainImageIndex: 0,
            map: { center: { latitude: 0, longitude: 0 }, zoom: 2, markers: [] },
            defaultCurrency,
            currencyRates: {},
            mapNeedsUpdate: false,
        };

        const tripId = await TripService.createTrip(tripData);
        console.log(`✅ New trip created: ${tripId} ("${tripName}")`);

        // Create the entity inside the new trip
        await this.createTripEntity(tripId, userId, parsed);

        // Update the booking with the trip ID
        const bookingRef = doc(db, this.getCollectionPath(userId), bookingId);
        await setDoc(bookingRef, {
            matchedTripId: tripId,
            approvalStatus: ApprovalStatus.APPROVED,
            status: 'attached',
            processedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        }, { merge: true });

        // Send notification about the new trip created from booking
        try {
            const bookingTypeName =
                parsed.bookingType === BookingType.ACCOMMODATION ? 'accommodation' :
                    parsed.bookingType === BookingType.FLIGHT ? 'flight' :
                        parsed.bookingType === BookingType.CAR_RENTAL ? 'car rental' : 'booking';
            await NotificationService.createNotification({
                userId,
                type: NotificationType.BOOKING_IMPORTED,
                title: 'New Trip Created',
                message: `Trip "${tripName}" was automatically created from your ${bookingTypeName} booking`,
                read: false,
                actionUrl: `/trips/${tripId}`,
                metadata: { tripId, tripName },
            });
        } catch (e) {
            console.warn('⚠️ Could not send trip created notification:', e);
        }

        return tripId;
    }

    /**
     * Derive a trip's start/end date range from a parsed booking, falling back
     * to a 7-day window when the booking has no end date. Shared by
     * createTripFromBooking and the auto-create path in processEmails so both
     * produce identical trip ranges.
     */
    private static deriveTripDates(parsed: ParsedBookingData): { startDate: Date; endDate: Date } {
        let startDate: Date;
        let endDate: Date;

        if (parsed.checkInDate) {
            startDate = new Date(parsed.checkInDate);
        } else if (parsed.departureTime) {
            startDate = new Date(parsed.departureTime);
        } else if (parsed.pickupDate) {
            startDate = new Date(parsed.pickupDate);
        } else {
            startDate = new Date();
        }

        if (parsed.checkOutDate) {
            endDate = new Date(parsed.checkOutDate);
        } else if (parsed.arrivalTime) {
            endDate = new Date(parsed.arrivalTime);
        } else if (parsed.returnDate) {
            endDate = new Date(parsed.returnDate);
        } else {
            endDate = new Date(startDate);
            endDate.setDate(endDate.getDate() + 7); // Default 7-day trip
        }

        return { startDate, endDate };
    }

    /**
     * Resolve the parsed destination country name to a known {name, code}
     * Country so the auto-created trip has a proper country (flag, currency,
     * map/region context) instead of being left blank. Matches case-insensitively
     * against the full country list, with a few common aliases.
     */
    private static resolveCountry(parsed: ParsedBookingData): (typeof COUNTRIES)[number] | null {
        const raw = parsed.country?.trim();
        if (!raw) return null;

        const aliases: Record<string, string> = {
            'usa': 'United States',
            'us': 'United States',
            'united states of america': 'United States',
            'uk': 'United Kingdom',
            'uae': 'United Arab Emirates',
            'holland': 'Netherlands',
            'south korea': 'South Korea',
            'czechia': 'Czech Republic',
        };
        const normalized = (aliases[raw.toLowerCase()] || raw).toLowerCase();

        return (
            COUNTRIES.find(c => c.name.toLowerCase() === normalized) ||
            // Fall back to a 2-letter ISO code if the parser returned one.
            (raw.length === 2
                ? COUNTRIES.find(c => c.code.toLowerCase() === raw.toLowerCase())
                : undefined) ||
            null
        );
    }

    /**
     * Pick the best "where is this trip" hint from a parsed booking. For flights
     * the parser's own `city`/`country` are unreliable on round trips (it often
     * returns the ORIGIN or leaves country blank), so prefer the arrival of the
     * first outbound segment — that's the real destination when departing home.
     */
    private static pickDestinationHint(parsed: ParsedBookingData): string | null {
        let hint: string | undefined;
        switch (parsed.bookingType) {
            case BookingType.FLIGHT:
                hint = parsed.segments?.[0]?.toLocation
                    || parsed.toLocation
                    || parsed.city
                    || undefined;
                break;
            case BookingType.CAR_RENTAL:
                hint = parsed.pickupLocation || parsed.city || undefined;
                break;
            case BookingType.ACCOMMODATION:
            default:
                hint = parsed.propertyAddress || parsed.city || parsed.toLocation || undefined;
                break;
        }
        const cleaned = (hint || '').trim();
        return cleaned ? cleaned : null;
    }

    /**
     * Deterministically resolve a Country by geocoding a destination string
     * (city/airport/address) via the Maps gateway and reading the country
     * component off the result. Used when the parser didn't return a usable
     * `country` — common for round-trip flights — so the auto-created trip
     * still gets a proper country (flag, currency, map/AI-boundary context)
     * instead of being left blank. Returns null on any failure.
     */
    private static async resolveCountryFromDestination(
        destination: string
    ): Promise<(typeof COUNTRIES)[number] | null> {
        // Strip trailing airport codes like "Bratislava (BTS)" that can confuse geocoding.
        const query = destination.replace(/\s*\([A-Z]{3}\)\s*$/, '').trim();
        if (!query) return null;

        try {
            const data = await ApiClient.get<{ results?: any[]; status?: string }>(
                `/maps/geocode?address=${encodeURIComponent(query)}`
            );
            const components: any[] = data?.results?.[0]?.address_components || [];
            const countryComp = components.find(
                (c) => Array.isArray(c.types) && c.types.includes('country')
            );
            if (!countryComp) {
                console.warn(`⚠️ Geocode found no country for destination "${query}"`);
                return null;
            }
            const code = (countryComp.short_name || '').toUpperCase();
            const name = (countryComp.long_name || '').toLowerCase();
            const match =
                COUNTRIES.find(c => c.code.toUpperCase() === code) ||
                COUNTRIES.find(c => c.name.toLowerCase() === name) ||
                null;
            if (match) {
                console.log(`🌍 Resolved country from destination "${query}" → ${match.name}`);
            }
            return match;
        } catch (e) {
            console.warn(`⚠️ Could not geocode destination "${destination}" for country:`, e);
            return null;
        }
    }

    private static generateTripName(
        parsed: ParsedBookingData,
        country?: (typeof COUNTRIES)[number] | null
    ): string {
        const parts: string[] = [];

        // Prefer the resolved country name; fall back to the parser's
        // country/city when we couldn't resolve one.
        if (country) parts.push(country.name);
        else if (parsed.country) parts.push(parsed.country);
        else if (parsed.city) parts.push(parsed.city);
        else if (parsed.toLocation) parts.push(parsed.toLocation);

        if (parts.length === 0) parts.push('New Trip');

        // Add date context
        const dateStr = parsed.checkInDate || parsed.departureTime || parsed.pickupDate;
        if (dateStr) {
            const d = new Date(dateStr);
            const month = d.toLocaleDateString('en-US', { month: 'short' });
            const year = d.getFullYear();
            parts.push(`${month} ${year}`);
        }

        return parts.join(' - ');
    }

    // ─── Approve a booking and create trip entities ──────────────

    static async approveAndAttach(
        bookingId: string,
        tripId: string,
        userId: string,
        parsed: ParsedBookingData
    ): Promise<void> {
        // Create entity in the trip
        await this.createTripEntity(tripId, userId, parsed);

        // Update booking status in mobile-compatible path
        const bookingRef = doc(db, this.getCollectionPath(userId), bookingId);
        await setDoc(bookingRef, {
            matchedTripId: tripId,
            approvalStatus: ApprovalStatus.APPROVED,
            status: 'attached',
            processedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        }, { merge: true });

        // Send notification about the approved booking
        try {
            const trip = await TripService.getTrip(tripId);
            await EmailProcessingService._sendBookingNotification(
                userId, parsed, tripId, trip?.name || 'your trip', false
            );
        } catch (e) {
            console.warn('⚠️ Could not send approval notification:', e);
        }

        console.log(`✅ Booking ${bookingId} approved and attached to trip ${tripId}`);
    }

    // ─── Booking notification helper ─────────────────────────────

    private static async _sendBookingNotification(
        userId: string,
        parsed: ParsedBookingData,
        tripId: string,
        tripName: string,
        isAutoMatch: boolean
    ): Promise<void> {
        try {
            let title: string;
            let message: string;
            let stableId: string;
            const action = isAutoMatch ? 'automatically added to' : 'added to';

            switch (parsed.bookingType) {
                case BookingType.ACCOMMODATION: {
                    const checkIn = parsed.checkInDate
                        ? new Date(parsed.checkInDate).toLocaleDateString('en-GB') : '';
                    const checkOut = parsed.checkOutDate
                        ? new Date(parsed.checkOutDate).toLocaleDateString('en-GB') : '';
                    const dateInfo = checkIn && checkOut ? ` (${checkIn} - ${checkOut})` : '';
                    title = 'Accommodation Added to Trip';
                    message = `${parsed.propertyName || 'Accommodation'}${dateInfo} has been ${action} ${tripName}`;
                    stableId = parsed.confirmationNumber || parsed.propertyName || 'accommodation';
                    break;
                }
                case BookingType.FLIGHT: {
                    const from = (parsed as any).fromLocation || (parsed as any).segments?.[0]?.fromLocation || '';
                    const to = (parsed as any).toLocation || (parsed as any).segments?.[0]?.toLocation || '';
                    const flightNum = (parsed as any).flightNumber || (parsed as any).segments?.[0]?.flightNumber || '';
                    const depDate = (parsed as any).departureTime
                        ? ` on ${new Date((parsed as any).departureTime).toLocaleDateString('en-GB')}` : '';
                    title = 'Flight Added to Trip';
                    message = `${flightNum ? `${flightNum} ` : ''}(${from} → ${to})${depDate} has been ${action} ${tripName}`;
                    stableId = parsed.confirmationNumber || flightNum || `${from}-${to}`;
                    break;
                }
                case BookingType.CAR_RENTAL: {
                    const company = (parsed as any).company || parsed.vendor || 'Car Rental';
                    const vehicleInfo = (parsed as any).vehicleType ? ` (${(parsed as any).vehicleType})` : '';
                    const pickupDate = (parsed as any).pickupDate
                        ? new Date((parsed as any).pickupDate).toLocaleDateString('en-GB') : '';
                    title = 'Car Rental Added to Trip';
                    message = `${company}${vehicleInfo} has been ${action} ${tripName}`;
                    stableId = parsed.confirmationNumber || `${company}_${pickupDate}`;
                    break;
                }
                case BookingType.ACTIVITY: {
                    const activityName = parsed.propertyName || parsed.vendor || 'Activity';
                    const dateInfo = parsed.checkInDate
                        ? ` on ${new Date(parsed.checkInDate).toLocaleDateString('en-GB')}` : '';
                    title = 'Activity Added to Trip';
                    message = `${activityName}${dateInfo} has been ${action} ${tripName}`;
                    stableId = parsed.confirmationNumber || parsed.propertyName || 'activity';
                    break;
                }
                default:
                    return;
            }

            // Stable key prevents duplicate notifications for the same booking+trip
            const dedupeKey = `booking_notif_${parsed.bookingType}_${tripId}_${stableId}`
                .toLowerCase().replace(/\s+/g, '_').substring(0, 200);

            const alreadySent = await NotificationService.notificationExists(userId, dedupeKey);
            if (alreadySent) return;

            await NotificationService.createNotification({
                userId,
                type: NotificationType.BOOKING_IMPORTED,
                title,
                message,
                read: false,
                actionUrl: `/trips/${tripId}`,
                dedupeKey,
                metadata: { tripId, tripName },
            });
        } catch (e) {
            console.warn('⚠️ Could not send booking notification:', e);
        }
    }

    // ─── Per-user last email check time ──────────────────────────

    /**
     * Get the last time we checked emails for this user.
     * Returns null if never checked before (first scan).
     */
    private static async getLastEmailCheckTime(userId: string): Promise<Date | null> {
        try {
            const docRef = doc(db, 'users', userId, 'email_config', 'settings');
            const snap = await getDoc(docRef);
            if (!snap.exists()) return null;
            const data = snap.data();
            if (data.lastEmailCheckTime instanceof Timestamp) {
                return data.lastEmailCheckTime.toDate();
            }
            if (typeof data.lastEmailCheckTime === 'string') {
                const d = new Date(data.lastEmailCheckTime);
                return isNaN(d.getTime()) ? null : d;
            }
            return null;
        } catch (error) {
            console.warn('⚠️ Could not read lastEmailCheckTime:', error);
            return null;
        }
    }

    /**
     * Save the current time as the last email check time for this user.
     */
    private static async setLastEmailCheckTime(userId: string): Promise<void> {
        try {
            const docRef = doc(db, 'users', userId, 'email_config', 'settings');
            await setDoc(docRef, {
                lastEmailCheckTime: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            }, { merge: true });
        } catch (error) {
            console.warn('⚠️ Could not save lastEmailCheckTime:', error);
        }
    }

    // ─── Download + store email attachments ──────────────────────

    /**
     * Max attachment size to store (10 MB). Matches the mobile pipeline and,
     * critically, the Storage security rule for email_attachments/** which
     * rejects uploads ≥ 10 MB.
     */
    private static readonly MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;

    /**
     * Download each Gmail attachment via the gateway and upload it to Firebase
     * Storage, returning permanent download URLs. Mirrors the mobile app's
     * GmailService._downloadAndStoreAttachments so both platforms expose the
     * original ticket PDFs/images the same way.
     */
    private static async downloadAndStoreAttachments(
        userId: string,
        emailId: string,
        attachments: RawEmailAttachment[] | undefined
    ): Promise<EmailAttachmentInfo[]> {
        if (!attachments || attachments.length === 0) return [];

        const result: EmailAttachmentInfo[] = [];
        for (const att of attachments) {
            if (!att.attachmentId) continue;
            if (att.size > this.MAX_ATTACHMENT_SIZE_BYTES) {
                console.warn(`⚠️ Skipping oversized attachment "${att.filename}" (${att.size} bytes)`);
                continue;
            }
            try {
                const data = await ApiClient.get<{ data?: string }>(
                    `/gmail/messages/${emailId}/attachments/${att.attachmentId}`
                );
                const base64Data = data.data;
                if (!base64Data) continue;

                // Gmail returns base64url-encoded bytes.
                const bytes = this.base64UrlToBytes(base64Data);
                const storageRef = ref(storage, `email_attachments/${userId}/${emailId}/${att.filename}`);
                await uploadBytes(storageRef, bytes, { contentType: att.mimeType });
                const url = await getDownloadURL(storageRef);

                result.push({
                    filename: att.filename,
                    url,
                    mimeType: att.mimeType,
                    size: att.size,
                });
                console.log(`📎 Stored attachment "${att.filename}" for email ${emailId}`);
            } catch (e) {
                console.warn(`⚠️ Could not store attachment "${att.filename}":`, e);
            }
        }
        return result;
    }

    /** MIME types the model can read inline: PDF documents and images. */
    private static isAiReadableAttachment(mimeType: string): boolean {
        const m = (mimeType || '').toLowerCase();
        return m === 'application/pdf' || m.startsWith('image/');
    }

    /**
     * Pick the most relevant attachment (a forwarded e-ticket/voucher PDF,
     * falling back to an image), fetch its bytes via the gateway and return a
     * standard-base64 payload for the multimodal AI parser — or null when there
     * is nothing suitable. Failures are swallowed so parsing gracefully degrades
     * to text-only. Mirrors the mobile app's _buildAttachmentPayload.
     */
    private static async buildAttachmentPayloadForAi(
        emailId: string,
        attachments: RawEmailAttachment[] | undefined
    ): Promise<{ base64: string; mimeType: string } | null> {
        if (!attachments || attachments.length === 0) return null;

        const candidates = attachments.filter(
            (a) =>
                a.attachmentId &&
                a.size > 0 &&
                a.size <= this.MAX_ATTACHMENT_SIZE_BYTES &&
                this.isAiReadableAttachment(a.mimeType)
        );
        if (candidates.length === 0) return null;

        // Prefer PDFs (e-tickets/vouchers) over images.
        candidates.sort((a, b) => {
            const rank = (m: string) => (m.toLowerCase() === 'application/pdf' ? 0 : 1);
            return rank(a.mimeType) - rank(b.mimeType);
        });
        const chosen = candidates[0];

        try {
            const data = await ApiClient.get<{ data?: string }>(
                `/gmail/messages/${emailId}/attachments/${chosen.attachmentId}`
            );
            if (!data.data) return null;
            // Gmail returns base64url; Gemini inlineData needs standard base64.
            const base64 = this.base64UrlToBase64(data.data);
            console.log(
                `🖇️ Sending attachment "${chosen.filename}" (${chosen.mimeType}) to AI parser`
            );
            return { base64, mimeType: chosen.mimeType };
        } catch (e) {
            console.warn(`⚠️ Could not attach "${chosen.filename}" to AI parse:`, e);
            return null;
        }
    }

    /** Convert a base64url string to standard base64 (with padding). */
    private static base64UrlToBase64(base64Url: string): string {
        let s = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const pad = s.length % 4;
        if (pad) s += '='.repeat(4 - pad);
        return s;
    }

    /** Decode a base64url string into a Uint8Array. */
    private static base64UrlToBytes(base64Url: string): Uint8Array {
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        return bytes;
    }

    // ─── Save raw email to shared collection ─────────────────────

    /**
     * Store the raw email in the Firestore `raw_emails` collection,
     * mirroring the mobile app structure so both platforms can read it.
     */
    private static async saveRawEmail(
        userId: string,
        email: RawEmailData,
        attachmentInfos: EmailAttachmentInfo[] = []
    ): Promise<void> {
        try {
            const docRef = doc(db, 'raw_emails', email.id);
            await setDoc(docRef, {
                id: email.id,
                userId,
                fromEmail: email.sender,
                toEmail: 'travelez.app@gmail.com',
                subject: email.subject,
                receivedAt: email.date.toISOString(),
                htmlContent: email.htmlBody || null,
                textContent: email.body || null,
                attachmentUrls: attachmentInfos.map((a) => a.url),
                attachmentInfos: attachmentInfos.map((a) => ({ ...a })),
                headers: {},
                messageId: email.id,
                createdAt: new Date().toISOString(),
                status: 'processed',
                processingNotes: null,
                relatedBookingIds: [],
                retryCount: 0,
                lastRetryAt: null,
            }, { merge: true });
        } catch (error) {
            console.warn(`⚠️ Could not save raw email ${email.id}:`, error);
        }
    }

    // ─── Update last processed timestamp (per-user) ──────────────
    // Stored under users/{uid}/app_config so each user has an independent
    // "last scan" cursor. The global app_config/email_processing doc is
    // admin-only per Firestore rules, so writing it here failed with a
    // permissions error for regular users.

    private static async updateLastProcessedTime(userId: string): Promise<void> {
        try {
            const docRef = doc(db, 'users', userId, 'app_config', 'email_processing');
            await setDoc(docRef, {
                last_processed_time: serverTimestamp(),
            }, { merge: true });
        } catch (error) {
            console.warn('⚠️ Could not update last processed time:', error);
        }
    }
}

export default EmailProcessingService;
